/* eslint-disable @typescript-eslint/no-explicit-any */
import { Allocation } from "api/securities/types";
import { getNameFromBackendTranslations } from "./transactions";

export const getSectorFromAllocations = (
  allocations: Allocation[],
  locale: string,
  assetType = "ASSET"
) => {
  const sector = allocations.find(
    (allocation) => allocation.group.code === assetType
  )?.sector;

  if (!sector) return undefined;
  const { namesAsMap, name: typeName } = sector;
  return getNameFromBackendTranslations(typeName, locale, namesAsMap);
};

export const getSectorPriorityFromAllocations = (
  allocations: Allocation[],
  assetType = "ASSET"
) =>
  allocations.find((allocation) => allocation.group.code === assetType)?.sector
    .priority;

export const getSectorIdFromAllocations = (
  allocations: Allocation[],
  assetType = "ASSET"
) => {
  const code = allocations.find(
    (allocation) => allocation.group.code === assetType
  )?.sector.code;

  return code;
};

/**
 * Every sector code a security is allocated to under `assetType`, not just the
 * first. Mixed funds carry several ASSET allocations (e.g. "Pappersfonden" is
 * SA + NR + ALTER + CASH + GA), so anything that asks "does this security
 * belong to sector X" has to look past `getSectorIdFromAllocations`, which
 * answers only with the first one.
 */
export const getSectorIdsFromAllocations = (
  allocations: Allocation[],
  assetType = "ASSET"
): string[] =>
  Array.from(
    new Set(
      allocations
        .filter((allocation) => allocation.group.code === assetType)
        .map((allocation) => allocation.sector.code)
        .filter((code): code is string => Boolean(code))
    )
  );

export const getPathToValue = (
  allocations: Allocation[],
  locale: string,
  assetType = "ASSET"
): string[] => {
  let path: string[] = [];
  let found = false;
  let sector = allocations.find(
    (allocation) => allocation.group.code === assetType
  )?.sector;

  const traverseObject = (obj: any, currentPath: string[]): void => {
    for (const key in obj) {
      const value = obj[key];

      if (value === sector) {
        path = currentPath.concat(key);
        found = true;
        return;
      }

      if (typeof value === "object" && value !== null) {
        traverseObject(value, currentPath.concat(key));
      }
    }
  };

  for (let i = 0; i < allocations.length; i++) {
    const allocation = allocations[i];
    const allocationPath = ["allocations", i.toString(), "sector"];

    if (allocation.group.code === assetType) {
      path = allocationPath;
      sector = allocation.sector;
      found = true;
      break;
    }

    traverseObject(allocation, allocationPath);
    if (found) break;
  }

  if (found && sector) {
    const { namesAsMap, name: typeName } = sector;
    const translatedName = getNameFromBackendTranslations(
      typeName,
      locale,
      namesAsMap
    );
    if (translatedName) {
      const localePath = ["namesAsMap", locale.replace("-", "_")];
      path.push(...localePath);
    } else {
      path.push("name");
    }
  }

  return path;
};
